import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useState } from "react";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import MyDocument from "./MyDocument";

const today = new Date();
const formattedDate = `${today.getDate().toString().padStart(2, "0")}/${(
  today.getMonth() + 1
)
  .toString()
  .padStart(2, "0")}/${today.getFullYear()}`;
const APP_PASSWORD = process.env.REACT_APP_ACCESS_PASSWORD || "romsey";
const createEmptyFields = () => ({
  title: "Mr",
  forename: "",
  surname: "",
  middleName: "",
  previousForename: "",
  previousSurname: "",
  dateOfBirth: "",
  townOfBirth: "",
  countryOfBirth: "UK",
  nationality: "",
  homeAddress: "",
  postcode: "",
  homeNumber: "",
  mobileNumber: "",
  email: "",
  livedAtAddress5Years: "Yes",
  previousAddresses: "",
});

const parseFormData = (data) => {
  // Extract name between MEMBERS and User details
  const nameMatch = data?.match(/MEMBERS(.*?)User details/s);
  const fullName = nameMatch ? nameMatch[1].trim() : "";

  // Split name into forename and surname at last space
  const lastSpaceIndex = fullName.lastIndexOf(" ");
  const forename =
    lastSpaceIndex !== -1 ? fullName.slice(0, lastSpaceIndex) : fullName;
  const surname =
    lastSpaceIndex !== -1 ? fullName.slice(lastSpaceIndex + 1) : "";

  const previousFirstnameLine = data
    ?.split("\n")
    .find((line) => line.startsWith("Previous firstname: "));
  const previousLastnameLine = data
    ?.split("\n")
    .find((line) => line.startsWith("Previous lastname: "));
  const dobLine = data?.split("\n").find((line) => line.startsWith("DoB: "));
  const previousAddressesLine = data
    ?.split("\n")
    .find((line) => line.startsWith("Previous addresses: "));
  const nationalityLine = data
    ?.split("\n")
    .find((line) => line.startsWith("Nationality: "));
  const placeOfBirthLine = data
    ?.split("\n")
    .find((line) => line.startsWith("Place of birth: "));
  const titleLine = data
    ?.split("\n")
    .find((line) => line.startsWith("Title: "));
  const middleNameLine = data
    ?.split("\n")
    .find((line) => line.startsWith("Middle name: "));
  const emailLine = data
    ?.split("\n")
    .find((line) => line.startsWith("Email: "));
  const telLine = data?.split("\n").find((line) => line.startsWith("Tel: "));
  const addressLine = data
    ?.split("\n")
    .find((line) => line.startsWith("Address: "));

  const previousForename = previousFirstnameLine
    ? previousFirstnameLine.replace("Previous firstname: ", "").trim()
    : "";
  const previousSurname = previousLastnameLine
    ? previousLastnameLine.replace("Previous lastname: ", "").trim()
    : "";
  const dateOfBirth = dobLine ? dobLine.replace("DoB: ", "").trim() : "";
  const previousAddresses = previousAddressesLine
    ? previousAddressesLine.replace("Previous addresses: ", "").trim()
    : "";
  const nationality = nationalityLine
    ? nationalityLine.replace("Nationality: ", "").trim()
    : "";
  const title = titleLine ? titleLine.replace("Title: ", "").trim() : "Mr";
  let middleName = middleNameLine
    ? middleNameLine.replace("Middle name: ", "").trim()
    : "";

  let townOfBirth = "";
  let countryOfBirth = "UK";
  if (placeOfBirthLine) {
    const fullPlaceOfBirth = placeOfBirthLine
      .replace("Place of birth: ", "")
      .trim();
    if (fullPlaceOfBirth.includes(",")) {
      const [town, country] = fullPlaceOfBirth
        .split(",")
        .map((part) => part.trim());
      townOfBirth = town;
      countryOfBirth = country;
    } else if (fullPlaceOfBirth.includes(" UK")) {
      const [town] = fullPlaceOfBirth.split(" UK");
      townOfBirth = town.trim();
      countryOfBirth = "UK";
    } else {
      townOfBirth = fullPlaceOfBirth;
    }
  }

  let phoneNumber = telLine ? telLine.replace("Tel: ", "").trim() : "";
  const fullAddress = addressLine ? addressLine.replace("Address: ", "").trim() : "";
  const email = emailLine ? emailLine.replace("Email: ", "").trim() : "";

  phoneNumber = phoneNumber.includes("(")
    ? "(" + phoneNumber.split("(")[1]
    : phoneNumber;

  const mobileNumber = phoneNumber.startsWith("(+44) 07") ? phoneNumber : "";
  const homeNumber = phoneNumber.startsWith("(+44) 07") ? "" : phoneNumber;

  const postcodeRegex = /[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}/i;
  const postcodeMatch = fullAddress.match(postcodeRegex);
  const postcode = postcodeMatch ? postcodeMatch[0].trim() : "";
  const homeAddress = postcodeMatch
    ? fullAddress
        .replace(postcodeMatch[0], "")
        .trim()
        .replace(/,?\s*$/, "")
    : fullAddress;

  let normalizedForename = forename;
  if (!middleName) {
    const nameParts = forename.split(/\s+/).filter(Boolean);
    if (nameParts.length > 1) {
      normalizedForename = nameParts[0];
      middleName = nameParts.slice(1).join(" ");
    }
  }

  return {
    title,
    forename: normalizedForename,
    surname,
    middleName,
    previousForename,
    previousSurname,
    dateOfBirth,
    townOfBirth,
    countryOfBirth,
    nationality,
    homeAddress,
    postcode,
    homeNumber,
    mobileNumber,
    email,
    livedAtAddress5Years: previousAddresses ? "No" : "Yes",
    previousAddresses,
  };
};

const buildPdfFileName = (fields) => {
  const surname = (fields?.surname || "").trim().toUpperCase();
  const firstInitial = (fields?.forename || "").trim().charAt(0).toUpperCase();
  const safeSurname = surname || "UNKNOWN";
  const safeInitial = firstInitial || "X";
  return `${safeSurname} ${safeInitial} - Screening Form.pdf`;
};

const PrivacyBanner = () => (
  <div className="privacy-banner">
    Privacy notice: no personal details are kept after the form is created.
  </div>
);

const HomePage = () => {
  const [inputMode, setInputMode] = useState("paste");
  const [formData, setFormData] = useState("");
  const [manualData, setManualData] = useState(createEmptyFields);
  const [reviewData, setReviewData] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    setReviewData(parseFormData(formData));
  };

  const handleReviewChange = (field, value) => {
    setReviewData((prev) => ({ ...prev, [field]: value }));
  };
  const handleManualChange = (field, value) => {
    setManualData((prev) => ({ ...prev, [field]: value }));
  };
  const handleManualSubmit = (e) => {
    e.preventDefault();
    setReviewData({
      ...manualData,
      livedAtAddress5Years:
        manualData.previousAddresses.trim().length > 0
          ? "No"
          : manualData.livedAtAddress5Years,
    });
  };

  const handleFinalSubmit = (e) => {
    e.preventDefault();
    if (!reviewData) return;
    navigate("/pdf", { state: { fields: reviewData } });
  };

  const handleBackToInput = () => {
    setReviewData(null);
  };

  return (
    <div className="app-shell">
      <PrivacyBanner />
      <div className="page-card">
        <h1 className="page-title">Police Form Compiler</h1>
      {!reviewData ? (
          <div>
            <div className="tab-row">
              <button
                type="button"
                className={`tab-btn ${inputMode === "paste" ? "active" : ""}`}
                onClick={() => setInputMode("paste")}
              >
                Paste and Parse
              </button>
              <button
                type="button"
                className={`tab-btn ${inputMode === "manual" ? "active" : ""}`}
                onClick={() => setInputMode("manual")}
              >
                Manual Entry
              </button>
            </div>
            {inputMode === "paste" ? (
              <form onSubmit={handleSubmit} className="form-grid">
                <p className="page-intro">
                  Follow these steps before pasting:
                </p>
                <ol className="steps-list">
                  <li>Step 1 - Login into Range Mate.</li>
                  <li>Step 2 - Locate the member page.</li>
                  <li>
                    Step 3 - Copy everything from the member photo down to the
                    bottom of the user details section.
                  </li>
                  <li>
                    Step 4 - Paste the copied content in the box below, then click
                    Review Fields.
                  </li>
                </ol>
                <p className="page-intro">
                  The parser will extract the key fields for you to review before
                  generating the final PDF.
                </p>
                <textarea
                  className="input-textarea"
                  value={formData}
                  onChange={(e) => setFormData(e.target.value)}
                  placeholder="Enter data"
                />
                <div className="button-row">
                  <button type="submit" className="btn btn-primary">
                    Review Fields
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleManualSubmit} className="form-grid">
                <p className="page-intro">
                  Step 1: Type required fields directly if you do not have a source
                  paste.
                </p>
                <input
                  className="input-control"
                  value={manualData.title}
                  onChange={(e) => handleManualChange("title", e.target.value)}
                  placeholder="Title"
                />
                <input
                  className="input-control"
                  value={manualData.forename}
                  onChange={(e) => handleManualChange("forename", e.target.value)}
                  placeholder="Forename(s)"
                  required
                />
                <input
                  className="input-control"
                  value={manualData.surname}
                  onChange={(e) => handleManualChange("surname", e.target.value)}
                  placeholder="Surname"
                  required
                />
                <input
                  className="input-control"
                  value={manualData.middleName}
                  onChange={(e) => handleManualChange("middleName", e.target.value)}
                  placeholder="Middle Name(s)"
                />
                <input
                  className="input-control"
                  value={manualData.previousSurname}
                  onChange={(e) =>
                    handleManualChange("previousSurname", e.target.value)
                  }
                  placeholder="Previous surname(s)"
                />
                <input
                  className="input-control"
                  value={manualData.previousForename}
                  onChange={(e) =>
                    handleManualChange("previousForename", e.target.value)
                  }
                  placeholder="Previous forename(s)"
                />
                <input
                  className="input-control"
                  value={manualData.dateOfBirth}
                  onChange={(e) => handleManualChange("dateOfBirth", e.target.value)}
                  placeholder="Date of Birth"
                  required
                />
                <input
                  className="input-control"
                  value={manualData.townOfBirth}
                  onChange={(e) => handleManualChange("townOfBirth", e.target.value)}
                  placeholder="Town of Birth"
                />
                <input
                  className="input-control"
                  value={manualData.countryOfBirth}
                  onChange={(e) =>
                    handleManualChange("countryOfBirth", e.target.value)
                  }
                  placeholder="Country of Birth"
                />
                <input
                  className="input-control"
                  value={manualData.nationality}
                  onChange={(e) => handleManualChange("nationality", e.target.value)}
                  placeholder="Nationality"
                />
                <input
                  className="input-control"
                  value={manualData.homeAddress}
                  onChange={(e) => handleManualChange("homeAddress", e.target.value)}
                  placeholder="Home address"
                  required
                />
                <input
                  className="input-control"
                  value={manualData.postcode}
                  onChange={(e) => handleManualChange("postcode", e.target.value)}
                  placeholder="Postcode"
                  required
                />
                <input
                  className="input-control"
                  value={manualData.email}
                  onChange={(e) => handleManualChange("email", e.target.value)}
                  placeholder="E-mail"
                  required
                />
                <input
                  className="input-control"
                  value={manualData.mobileNumber}
                  onChange={(e) =>
                    handleManualChange("mobileNumber", e.target.value)
                  }
                  placeholder="Mobile number"
                />
                <input
                  className="input-control"
                  value={manualData.homeNumber}
                  onChange={(e) => handleManualChange("homeNumber", e.target.value)}
                  placeholder="Home number"
                />
                <label className="field-label">
                  Have you lived at your home address for the last 5 years?
                </label>
                <select
                  className="input-control"
                  value={manualData.livedAtAddress5Years}
                  onChange={(e) =>
                    handleManualChange("livedAtAddress5Years", e.target.value)
                  }
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                {manualData.livedAtAddress5Years === "No" ? (
                  <textarea
                    className="input-textarea short"
                    value={manualData.previousAddresses}
                    onChange={(e) =>
                      handleManualChange("previousAddresses", e.target.value)
                    }
                    placeholder="Previous addresses (last 5 years)"
                  />
                ) : null}
                <div className="button-row">
                  <button type="submit" className="btn btn-primary">
                    Review Fields
                  </button>
                </div>
              </form>
            )}
          </div>
      ) : (
        <form
          onSubmit={handleFinalSubmit}
            className="form-grid"
        >
            <h2 className="section-title">Review and Edit Fields</h2>
            <p className="page-intro">
              Step 2: Check each field and make any corrections. Once confirmed,
              generate the final PDF for download.
            </p>
          <input
            className="input-control"
            value={reviewData.title}
            onChange={(e) => handleReviewChange("title", e.target.value)}
            placeholder="Title"
          />
          <input
            className="input-control"
            value={reviewData.forename}
            onChange={(e) => handleReviewChange("forename", e.target.value)}
            placeholder="Forename(s)"
          />
          <input
            className="input-control"
            value={reviewData.surname}
            onChange={(e) => handleReviewChange("surname", e.target.value)}
            placeholder="Surname"
          />
          <input
            className="input-control"
            value={reviewData.middleName}
            onChange={(e) => handleReviewChange("middleName", e.target.value)}
            placeholder="Middle Name(s)"
          />
          <input
            className="input-control"
            value={reviewData.previousSurname}
            onChange={(e) =>
              handleReviewChange("previousSurname", e.target.value)
            }
            placeholder="Previous surname(s)"
          />
          <input
            className="input-control"
            value={reviewData.previousForename}
            onChange={(e) =>
              handleReviewChange("previousForename", e.target.value)
            }
            placeholder="Previous forename(s)"
          />
          <input
            className="input-control"
            value={reviewData.dateOfBirth}
            onChange={(e) => handleReviewChange("dateOfBirth", e.target.value)}
            placeholder="Date of Birth"
          />
          <input
            className="input-control"
            value={reviewData.townOfBirth}
            onChange={(e) => handleReviewChange("townOfBirth", e.target.value)}
            placeholder="Town of Birth"
          />
          <input
            className="input-control"
            value={reviewData.countryOfBirth}
            onChange={(e) =>
              handleReviewChange("countryOfBirth", e.target.value)
            }
            placeholder="Country of Birth"
          />
          <input
            className="input-control"
            value={reviewData.nationality}
            onChange={(e) => handleReviewChange("nationality", e.target.value)}
            placeholder="Nationality"
          />
          <input
            className="input-control"
            value={reviewData.homeAddress}
            onChange={(e) => handleReviewChange("homeAddress", e.target.value)}
            placeholder="Home address"
          />
          <input
            className="input-control"
            value={reviewData.postcode}
            onChange={(e) => handleReviewChange("postcode", e.target.value)}
            placeholder="Postcode"
          />
          <input
            className="input-control"
            value={reviewData.homeNumber}
            onChange={(e) => handleReviewChange("homeNumber", e.target.value)}
            placeholder="Home number"
          />
          <input
            className="input-control"
            value={reviewData.mobileNumber}
            onChange={(e) => handleReviewChange("mobileNumber", e.target.value)}
            placeholder="Mobile number"
          />
          <input
            className="input-control"
            value={reviewData.email}
            onChange={(e) => handleReviewChange("email", e.target.value)}
            placeholder="E-mail"
          />
          <select
            className="input-control"
            value={reviewData.livedAtAddress5Years}
            onChange={(e) =>
              handleReviewChange("livedAtAddress5Years", e.target.value)
            }
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
          {reviewData.livedAtAddress5Years === "No" ? (
            <textarea
              className="input-textarea short"
              value={reviewData.previousAddresses}
              onChange={(e) =>
                handleReviewChange("previousAddresses", e.target.value)
              }
              placeholder="Previous addresses (last 5 years)"
            />
          ) : null}

            <div className="button-row">
              <button
                type="button"
                onClick={handleBackToInput}
                className="btn btn-secondary"
              >
              Back
            </button>
              <button type="submit" className="btn btn-primary">
                Generate Final PDF
              </button>
          </div>
        </form>
      )}
      </div>
    </div>
  );
};

const PDFPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const data = searchParams.get("data");
  const fieldsParam = searchParams.get("fields");
  let fields = location.state?.fields || null;

  if (!fields && fieldsParam) {
    try {
      fields = JSON.parse(fieldsParam);
    } catch (_error) {
      fields = null;
    }
  }

  const parsedFields = fields || parseFormData(data);
  const pdfFileName = buildPdfFileName(parsedFields);
  const document = (
    <MyDocument
      title={parsedFields.title}
      forename={parsedFields.forename}
      surname={parsedFields.surname}
      middleName={parsedFields.middleName}
      previousForename={parsedFields.previousForename}
      previousSurname={parsedFields.previousSurname}
      dateOfBirth={parsedFields.dateOfBirth}
      townOfBirth={parsedFields.townOfBirth}
      countryOfBirth={parsedFields.countryOfBirth}
      nationality={parsedFields.nationality}
      homeAddress={parsedFields.homeAddress}
      postcode={parsedFields.postcode}
      homeNumber={parsedFields.homeNumber}
      mobileNumber={parsedFields.mobileNumber}
      email={parsedFields.email}
      livedAtAddress5Years={parsedFields.livedAtAddress5Years}
      previousAddresses={parsedFields.previousAddresses}
      prohibitedPersonDate={formattedDate}
      submissionDate={formattedDate}
    />
  );

  return (
    <div className="pdf-page">
      <PrivacyBanner />
      <div className="pdf-toolbar">
        <p className="toolbar-note">
          Step 3: Download your compiled PDF or start a new submission.
        </p>
        <PDFDownloadLink document={document} fileName={pdfFileName}>
          {({ loading }) => (
            <span className="btn btn-primary">
              {loading ? "Preparing PDF..." : "Download PDF"}
            </span>
          )}
        </PDFDownloadLink>
        <Link to="/" className="btn btn-secondary">
          Start again
        </Link>
      </div>
      <PDFViewer className="pdf-viewer">
        {document}
      </PDFViewer>
    </div>
  );
};

const App = () => {
  const [passwordInput, setPasswordInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(
    () => sessionStorage.getItem("pfc_unlocked") === "true"
  );

  const handleUnlockSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === APP_PASSWORD) {
      sessionStorage.setItem("pfc_unlocked", "true");
      setIsUnlocked(true);
      setAuthError("");
      return;
    }
    setAuthError("Incorrect password.");
  };

  if (!isUnlocked) {
    return (
      <div className="app-shell">
        <PrivacyBanner />
        <div className="page-card password-card">
          <h1 className="page-title">Police Form Compiler</h1>
          <p className="page-intro">
            Enter the access password to use this tool.
          </p>
          <form onSubmit={handleUnlockSubmit} className="form-grid">
            <input
              className="input-control"
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Password"
            />
            {authError ? <p className="form-error">{authError}</p> : null}
            <div className="button-row">
              <button className="btn btn-primary" type="submit">
                Unlock
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/pdf" element={<PDFPage />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
